import { useEffect, useRef, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import type { TransitionDirection } from './components/PageTransition';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import PageTransition from './components/PageTransition';
import { useFeatureFlags, type FeatureFlagKey } from './hooks/useFeatureFlag';

// ── Eagerly loaded (landing page + auth + core shell) ──────────────
import LandingBeta from './components/landing/LandingBeta';
import Login from './components/Login';
import Signup from './components/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import { PlanProvider } from './contexts/PlanContext';
import { UnitsProvider } from './contexts/UnitsContext';
import { UserContextProvider } from './lib/UserContextProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkoutSessionProvider } from './contexts/WorkoutSessionContext';
import { supabase } from './lib/supabase';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useNavigationTracking } from './hooks/useNavigationTracking';
import XPToast from './components/gamification/XPToast';

// ── Lazy loaded (app pages — only loaded when navigated to) ────────
// Primary tabs (/app, /training, /analysis, /skills) mount through
// PrimaryTabsLayout's pager; the lazy refs below are kept for any
// non-primary references (none right now, but harmless).
const PrimaryTabsLayout = lazy(() => import('./components/PrimaryTabsLayout'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const Progress = lazy(() => import('./components/Progress'));
const Chat = lazy(() => import('./components/Chat'));
const Profile = lazy(() => import('./components/Profile'));
const Community = lazy(() => import('./components/Community'));
const Recovery = lazy(() => import('./components/Recovery'));
const Nutrition = lazy(() => import('./components/Nutrition'));
const HallOfFame = lazy(() => import('./components/HallOfFame'));
const InjuryLog = lazy(() => import('./components/InjuryLog'));
const OnboardingReveal = lazy(() => import('./components/OnboardingReveal'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const WeeklyChallenge = lazy(() => import('./components/WeeklyChallenge'));
const DuelArena = lazy(() => import('./components/DuelArena'));
const ForgeSession = lazy(() => import('./components/ForgeSession'));
const QuietV4Preview = lazy(() => import('./components/preview/QuietV4'));
const Terms = lazy(() => import('./components/legal/Terms'));
const Privacy = lazy(() => import('./components/legal/Privacy'));
const DataInventory = lazy(() => import('./components/legal/DataInventory'));

// Scroll restoration — must live inside the BrowserRouter. Forward
// nav scrolls to top; back/forward restores the saved Y. The hook
// itself is in src/hooks/useScrollRestore.ts; this is just the mount.
import { useScrollRestore } from './hooks/useScrollRestore';
function ScrollRestore() { useScrollRestore(); return null; }

// Native (iOS/Android) skips the marketing landing — the app icon is past
// the marketing funnel. ProtectedRoute on /app handles the auth branch:
// signed-in → dashboard, signed-out → /login. Web keeps the landing page.
// Band-aid until Wave 3 splits the marketing site from the app shell.
function RootRoute() {
  if (Capacitor.isNativePlatform()) {
    return <Navigate to="/app" replace />;
  }
  return <LandingBeta />;
}

// Wedge-cut gate: hides a v2 surface behind a feature flag, redirects to /app when off.
function FlagGate({ flag, children }: { flag: FeatureFlagKey; children: ReactNode }) {
  const { flags, loading } = useFeatureFlags();
  if (loading) return null;
  if (!flags[flag]) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// Gated startup: load duel/challenge services only when their flag is on.
function GameServicesStartup() {
  const { flags, loading } = useFeatureFlags();
  useEffect(() => {
    if (loading) return;
    if (flags.duels) {
      import('./services/duelExpiryService').then(({ runExpiryCheck }) => runExpiryCheck()).catch(() => {});
    }
    if (flags.challenge) {
      import('./services/weeklyChallengeScheduler').then(({ ensureWeeklyChallenge }) => ensureWeeklyChallenge()).catch(() => {});
    }
  }, [flags, loading]);
  return null;
}

// [F14-P1-E] Route depth map — deeper routes slide left on forward nav, slide right on back
const ROUTE_DEPTH: Record<string, number> = {
  '/': 0,
  '/login': 0,
  '/signup': 0,
  '/reset-password': 0,
  '/app': 1,
  '/analysis': 1,
  '/progress': 1,
  '/chat': 1,
  '/profile': 1,
  '/community': 1,
  '/training': 1,
  '/recovery': 1,
  '/nutrition': 1,
  '/injury-log': 1,
  '/hall-of-fame': 1,
  '/skills': 1,
  '/onboarding': 2,
  '/reveal': 2,
  '/upgrade': 2,
};

function AppRoutes() {
  useNavigationTracking();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const directionRef = useRef<TransitionDirection>('fade');

  // Compute direction synchronously — ref mutation during render is intentional (no state, no re-render)
  if (prevPathRef.current !== location.pathname) {
    const prevDepth = ROUTE_DEPTH[prevPathRef.current] ?? 1;
    const currDepth = ROUTE_DEPTH[location.pathname] ?? 1;
    directionRef.current = currDepth > prevDepth ? 'forward' : currDepth < prevDepth ? 'back' : 'fade';
    prevPathRef.current = location.pathname;
  }

  const direction = directionRef.current;

  // Primary tabs (/app, /training, /analysis, /skills) all render
  // through PrimaryTabsLayout's pager. We stabilize the
  // AnimatePresence key while on any primary tab so the pager stays
  // mounted across swipes/taps between them — otherwise AnimatePresence
  // would unmount and lose drag state on every tab change.
  const PRIMARY_PATHS = ['/app', '/training', '/analysis', '/skills'];
  const isPrimaryTab = PRIMARY_PATHS.includes(location.pathname);
  const transitionKey = isPrimaryTab ? '__primary-tabs__' : location.pathname;

  return (
    <>
    <XPToast />
    <AnimatePresence mode="wait" custom={direction}>
      <PageTransition key={transitionKey} custom={direction}>
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center font-body" style={{ backgroundColor: 'var(--color-canvas)', color: 'var(--color-text-secondary)' }}>Loading…</div>}>
        <Routes location={location}>
        <Route path="/" element={<RootRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        {/* Primary tabs share a single layout instance so the pager's
            drag state persists across tab swipes. The matched child
            route's element is null — PrimaryTabsLayout reads the URL
            and renders the centered tab itself. */}
        <Route
          element={
            <ProtectedRoute>
              <PrimaryTabsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/app" element={null} />
          <Route path="/training" element={null} />
          <Route path="/analysis" element={null} />
          <Route path="/skills" element={null} />
        </Route>
        <Route
          path="/progress"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Progress"><Progress /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Coach"><Chat /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/coach" element={<Navigate to="/chat" replace />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Profile"><Profile /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <FlagGate flag="community">
                <AppLayout>
                  <ErrorBoundary fallbackName="Community"><Community /></ErrorBoundary>
                </AppLayout>
              </FlagGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recovery"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Recovery"><Recovery /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nutrition"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Nutrition"><Nutrition /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/injury-log"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary fallbackName="Injury Log"><InjuryLog /></ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hall-of-fame"
          element={
            <ProtectedRoute>
              <FlagGate flag="hallOfFame">
                <AppLayout>
                  <ErrorBoundary fallbackName="Hall of Fame"><HallOfFame /></ErrorBoundary>
                </AppLayout>
              </FlagGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenge"
          element={
            <ProtectedRoute>
              <FlagGate flag="challenge">
                <AppLayout>
                  <ErrorBoundary fallbackName="Weekly Challenge"><WeeklyChallenge /></ErrorBoundary>
                </AppLayout>
              </FlagGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/duels"
          element={
            <ProtectedRoute>
              <FlagGate flag="duels">
                <AppLayout>
                  <ErrorBoundary fallbackName="Duel Arena"><DuelArena /></ErrorBoundary>
                </AppLayout>
              </FlagGate>
            </ProtectedRoute>
          }
        />
        <Route path="/upgrade" element={<Navigate to="/chat" replace />} />
        <Route
          path="/reveal"
          element={
            <ProtectedRoute>
              <OnboardingReveal />
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/app-preview" element={<QuietV4Preview />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/data" element={<DataInventory />} />
        <Route
          path="/forge"
          element={
            <ProtectedRoute>
              <ForgeSession />
            </ProtectedRoute>
          }
        />
        </Routes>
        </Suspense>
      </PageTransition>
    </AnimatePresence>
    </>
  );
}

function App() {
  useEffect(() => {
    const handleFocus = () => { void supabase.auth.getSession(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Game services startup is now gated by feature flags via <GameServicesStartup />
  // mounted inside the provider tree (so it can read the signed-in user's flags).

  // Startup sound — plays once on first user interaction per session
  useEffect(() => {
    let played = false;
    const playStartup = () => {
      if (played) return;
      played = true;
      import('./utils/sounds').then(({ sounds }) => {
        try { sounds.startup(); } catch { /* intentionally empty */ }
      });
      document.removeEventListener('click', playStartup);
      document.removeEventListener('touchstart', playStartup);
    };
    document.addEventListener('click', playStartup, { once: true });
    document.addEventListener('touchstart', playStartup, { once: true });
    return () => {
      document.removeEventListener('click', playStartup);
      document.removeEventListener('touchstart', playStartup);
    };
  }, []);

  return (
    // reducedMotion="user" makes every framer-motion animation respect
    // the OS prefers-reduced-motion media query. Enabled = animations
    // collapse to instant duration. One-line accessibility win, replaces
    // a per-consumer useReducedMotion wrapper across hundreds of motion
    // calls. Research 2026-05-01.
    <MotionConfig reducedMotion="user">
      <ThemeProvider>
        <UserContextProvider>
          <UnitsProvider>
            <WorkoutSessionProvider>
              <BrowserRouter>
                <PlanProvider>
                  <GameServicesStartup />
                  <ScrollRestore />
                  <AppRoutes />
                </PlanProvider>
              </BrowserRouter>
            </WorkoutSessionProvider>
          </UnitsProvider>
        </UserContextProvider>
      </ThemeProvider>
    </MotionConfig>
  );
}

export default App;